// API client met envelope-unwrapping + canonical error-codes (PRD-00 §16.1, §16.2).
// Auto-refresh JWT bij een 401 die met een nieuw access-token te verhelpen is.

import { authStore } from "./auth";

const API_BASE = "/api";

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  meta: { requestId: string; timestamp: string };
}

interface ErrorEnvelope {
  success: false;
  error: {
    code: string;
    message: string;
    i18nKey: string;
    details?: unknown;
    traceId?: string;
  };
  meta?: { requestId: string; timestamp: string };
}

export class ApiError extends Error {
  constructor(
    public code: string,
    public i18nKey: string,
    public httpStatus: number,
    public details?: unknown,
    /**
     * Het traceId uit de foutenvelop. Hiermee kan iemand die belt één nummer
     * noemen waarmee het incident in de serverlogs terug te vinden is, in
     * plaats van "het deed het niet vanochtend".
     */
    public traceId?: string
  ) {
    super(code);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  tenantSlug?: string;
  signal?: AbortSignal;
  skipAuth?: boolean;
}

async function rawRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (!opts.skipAuth) {
    const token = authStore.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const tenantSlug = opts.tenantSlug ?? authStore.getTenantSlug();
  if (tenantSlug) headers["X-Tenant-Slug"] = tenantSlug;

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  let json: SuccessEnvelope<T> | ErrorEnvelope;
  try {
    json = await res.json();
  } catch {
    throw new ApiError("ERR_INTERNAL", "errors.internal", res.status);
  }

  if (!json.success) {
    throw new ApiError(
      json.error.code,
      json.error.i18nKey,
      res.status,
      json.error.details,
      json.error.traceId ?? json.meta?.requestId
    );
  }
  return json.data;
}

let refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = authStore.getRefreshToken();
    if (!refreshToken) throw new ApiError("ERR_AUTH_TOKEN_INVALID", "errors.auth.tokenInvalid", 401);
    const data = await rawRequest<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>("/v1/auth/refresh", {
      method: "POST",
      body: { refreshToken },
      skipAuth: true,
    });
    authStore.setTokens(data.accessToken, data.refreshToken);
  })();
  try {
    await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Foutcodes waarna het zin heeft een nieuw access-token te halen.
 *
 * `ERR_AUTH_TOKEN_EXPIRED` is de voor de hand liggende. `ERR_AUTH_TOKEN_INVALID`
 * hoorde er ook bij te staan, en dat het ontbrak was op 2026-08-20 zichtbaar te
 * maken met twee tabbladen: het access-token leeft in sessionStorage en dat is
 * per tabblad, terwijl het refresh-token in localStorage staat en gedeeld wordt.
 * In een tweede tabblad ging elk verzoek dus zonder Authorization-header de deur
 * uit, en dan antwoordt de backend met INVALID en niet met EXPIRED. Resultaat:
 * de route-guard liet de pagina toe, maar hij bleef op "Loading…" hangen —
 * geen foutmelding, geen doorverwijzing naar het inlogscherm.
 *
 * Verversen heeft alleen zin met een refresh-token in de hand; is dat er niet,
 * dan gooit `refreshAccessToken` en valt de code terug op uitloggen.
 */
const HERSTELBARE_AUTHFOUTEN = ["ERR_AUTH_TOKEN_EXPIRED", "ERR_AUTH_TOKEN_INVALID"];

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, opts);
  } catch (e) {
    if (
      e instanceof ApiError &&
      HERSTELBARE_AUTHFOUTEN.includes(e.code) &&
      !opts.skipAuth &&
      authStore.getRefreshToken()
    ) {
      try {
        await refreshAccessToken();
        return await rawRequest<T>(path, opts);
      } catch {
        authStore.clear();
        throw e;
      }
    }
    throw e;
  }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
